import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Bug, 
  Lightbulb, 
  Mail, 
  Send,
  AlertCircle,
  CheckCircle,
  MessageSquare,
  Heart,
  Info
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { IMAGES, bgStyle } from '@/config/images';

const ContactFeedback = () => {
  const token = localStorage.getItem('token');
  
  // Bug report form state
  const [bugForm, setBugForm] = useState({
    doing: '',
    expected: '',
    actual: '',
    details: ''
  });
  const [bugSubmitting, setBugSubmitting] = useState(false);
  const [bugSubmitted, setBugSubmitted] = useState(false);

  // Enhancement form state
  const [enhancementForm, setEnhancementForm] = useState({
    idea: '',
    reason: ''
  });
  const [enhancementSubmitting, setEnhancementSubmitting] = useState(false);
  const [enhancementSubmitted, setEnhancementSubmitted] = useState(false);

  // Contact form state
  const [contactForm, setContactForm] = useState({
    subject: '',
    message: ''
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);

  const handleBugSubmit = async (e) => {
    e.preventDefault();
    if (!bugForm.doing || !bugForm.expected || !bugForm.actual) {
      toast.error('Please fill in all required fields');
      return;
    }
    setBugSubmitting(true);
    try {
      await axios.post(`${API}/feedback/bug`, bugForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBugSubmitted(true);
      setBugForm({ doing: '', expected: '', actual: '', details: '' });
      toast.success('Bug report submitted. Thank you!');
      window.location.href = `mailto:Admin@Cnc-helpdesk.com?subject=${encodeURIComponent('CNC-Helpdesk Bug Report')}`;
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setBugSubmitting(false);
    }
  };

  const handleEnhancementSubmit = async (e) => {
    e.preventDefault();
    if (!enhancementForm.idea) {
      toast.error('Please describe your idea');
      return;
    }
    setEnhancementSubmitting(true);
    try {
      await axios.post(`${API}/feedback/enhancement`, enhancementForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnhancementSubmitted(true);
      setEnhancementForm({ idea: '', reason: '' });
      toast.success('Suggestion submitted. Thank you!');
      window.location.href = `mailto:Admin@Cnc-helpdesk.com?subject=${encodeURIComponent('CNC-Helpdesk Suggestion')}`;
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setEnhancementSubmitting(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.subject || !contactForm.message) {
      toast.error('Please fill in all fields');
      return;
    }
    setContactSubmitting(true);
    try {
      await axios.post(`${API}/feedback/contact`, contactForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContactSubmitted(true);
      setContactForm({ subject: '', message: '' });
      toast.success('Message sent. Thank you!');
      window.location.href = `mailto:Admin@Cnc-helpdesk.com?subject=${encodeURIComponent('CNC-Helpdesk Contact')}`;
    } catch (error) {
      toast.error('Failed to send. Please try again.');
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <div 
      data-testid="contact-feedback-page" 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.CONTACT_FEEDBACK)}
    >
      <div className="fixed inset-0 bg-black/75 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Page Header */}
          <div className="text-center mb-12">
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-2">GET IN TOUCH</div>
            <h1 className="text-4xl md:text-5xl font-heading font-black tracking-tight text-[#e67e22] mb-4">
              CONTACT & FEEDBACK
            </h1>
            <div className="max-w-2xl mx-auto space-y-3">
              <p className="text-lg text-[#e67e22]">
                CNC-Helpdesk is actively developed and maintained.
              </p>
              <p className="text-[#e67e22]/80">
                User feedback plays an important role in improving the platform.
                This page provides a simple way to report issues, suggest enhancements, or get in touch.
              </p>
            </div>
          </div>

          {/* Bug Reporting Section */}
          <Card className="bg-black/60 backdrop-blur-sm border-red-500/30 border-2">
            <CardHeader>
              <CardTitle className="text-2xl text-white flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <Bug className="w-6 h-6 text-red-400" />
                </div>
                Report a Bug
              </CardTitle>
              <CardDescription className="text-[#a3a3a3] text-base">
                If you encounter something that does not behave as expected, please let us know.
                <br />
                Bug reports help improve CNC-Helpdesk for everyone.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {bugSubmitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Thank you!</h3>
                  <p className="text-[#a3a3a3] mb-4">Your bug report has been received.</p>
                  <Button 
                    variant="outline" 
                    onClick={() => setBugSubmitted(false)}
                    className="border-[#3a3a3a] text-[#d6d6d6]"
                  >
                    Report another issue
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleBugSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm text-[#a3a3a3] mb-1 block">
                      What were you doing? <span className="text-red-400">*</span>
                    </label>
                    <Input
                      placeholder="e.g., Calculating feed rates for aluminum..."
                      value={bugForm.doing}
                      onChange={(e) => setBugForm(prev => ({ ...prev, doing: e.target.value }))}
                      className="bg-black/50 border-[#3a3a3a] text-white placeholder:text-[#a3a3a3]"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm text-[#a3a3a3] mb-1 block">
                      What did you expect to happen? <span className="text-red-400">*</span>
                    </label>
                    <Input
                      placeholder="e.g., The calculator should show results..."
                      value={bugForm.expected}
                      onChange={(e) => setBugForm(prev => ({ ...prev, expected: e.target.value }))}
                      className="bg-black/50 border-[#3a3a3a] text-white placeholder:text-[#a3a3a3]"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm text-[#a3a3a3] mb-1 block">
                      What actually happened? <span className="text-red-400">*</span>
                    </label>
                    <Input
                      placeholder="e.g., The page showed an error message..."
                      value={bugForm.actual}
                      onChange={(e) => setBugForm(prev => ({ ...prev, actual: e.target.value }))}
                      className="bg-black/50 border-[#3a3a3a] text-white placeholder:text-[#a3a3a3]"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm text-[#a3a3a3] mb-1 block">
                      Additional details (optional)
                    </label>
                    <Textarea
                      placeholder="Any extra information that might help us understand the issue..."
                      value={bugForm.details}
                      onChange={(e) => setBugForm(prev => ({ ...prev, details: e.target.value }))}
                      className="bg-black/50 border-[#3a3a3a] text-white placeholder:text-[#a3a3a3] min-h-[80px]"
                    />
                  </div>
                  
                  <p className="text-[#a3a3a3] text-sm flex items-start gap-2">
                    <Info className="w-4 h-4 mt-0.5 shrink-0" />
                    Screenshots or clear descriptions are very helpful.
                  </p>
                  
                  <Button 
                    type="submit"
                    disabled={bugSubmitting}
                    className="w-full bg-red-500 hover:bg-red-600 text-white font-bold"
                  >
                    {bugSubmitting ? 'Submitting...' : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Bug Report
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Enhancement Suggestions Section */}
          <Card className="bg-black/60 backdrop-blur-sm border-amber-500/30 border-2">
            <CardHeader>
              <CardTitle className="text-2xl text-white flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <Lightbulb className="w-6 h-6 text-amber-400" />
                </div>
                Suggest an Enhancement
              </CardTitle>
              <CardDescription className="text-[#a3a3a3] text-base">
                Have an idea that would make CNC-Helpdesk more useful or easier to use?
                <br />
                We actively review enhancement suggestions and use them to guide future development.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {enhancementSubmitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Thank you!</h3>
                  <p className="text-[#a3a3a3] mb-4">Your suggestion has been received.</p>
                  <Button 
                    variant="outline" 
                    onClick={() => setEnhancementSubmitted(false)}
                    className="border-[#3a3a3a] text-[#d6d6d6]"
                  >
                    Submit another idea
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleEnhancementSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm text-[#a3a3a3] mb-1 block">
                      Feature idea or improvement <span className="text-amber-400">*</span>
                    </label>
                    <Textarea
                      placeholder="Describe your idea for a new feature or improvement..."
                      value={enhancementForm.idea}
                      onChange={(e) => setEnhancementForm(prev => ({ ...prev, idea: e.target.value }))}
                      className="bg-black/50 border-[#3a3a3a] text-white placeholder:text-[#a3a3a3] min-h-[100px]"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm text-[#a3a3a3] mb-1 block">
                      Why would this be useful? (optional)
                    </label>
                    <Textarea
                      placeholder="Help us understand how this would improve your workflow..."
                      value={enhancementForm.reason}
                      onChange={(e) => setEnhancementForm(prev => ({ ...prev, reason: e.target.value }))}
                      className="bg-black/50 border-[#3a3a3a] text-white placeholder:text-[#a3a3a3] min-h-[80px]"
                    />
                  </div>
                  
                  <p className="text-[#a3a3a3] text-sm flex items-start gap-2">
                    <Info className="w-4 h-4 mt-0.5 shrink-0" />
                    Not all suggestions can be implemented, but all are reviewed.
                  </p>
                  
                  <Button 
                    type="submit"
                    disabled={enhancementSubmitting}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold"
                  >
                    {enhancementSubmitting ? 'Submitting...' : (
                      <>
                        <Lightbulb className="w-4 h-4 mr-2" />
                        Submit Suggestion
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* General Contact Section */}
          <Card className="bg-black/60 backdrop-blur-sm border-blue-500/30 border-2">
            <CardHeader>
              <CardTitle className="text-2xl text-white flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Mail className="w-6 h-6 text-blue-400" />
                </div>
                General Contact
              </CardTitle>
              <CardDescription className="text-[#a3a3a3] text-base">
                CNC-Helpdesk is built and maintained by an independent developer.
                <br />
                If you have general feedback, comments, or questions about the platform, you are welcome to get in touch.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {contactSubmitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Message sent!</h3>
                  <p className="text-[#a3a3a3] mb-4">Thank you for reaching out.</p>
                  <Button 
                    variant="outline" 
                    onClick={() => setContactSubmitted(false)}
                    className="border-[#3a3a3a] text-[#d6d6d6]"
                  >
                    Send another message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm text-[#a3a3a3] mb-1 block">
                      Subject <span className="text-blue-400">*</span>
                    </label>
                    <Input
                      placeholder="What's this about?"
                      value={contactForm.subject}
                      onChange={(e) => setContactForm(prev => ({ ...prev, subject: e.target.value }))}
                      className="bg-black/50 border-[#3a3a3a] text-white placeholder:text-[#a3a3a3]"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm text-[#a3a3a3] mb-1 block">
                      Message <span className="text-blue-400">*</span>
                    </label>
                    <Textarea
                      placeholder="Your message..."
                      value={contactForm.message}
                      onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                      className="bg-black/50 border-[#3a3a3a] text-white placeholder:text-[#a3a3a3] min-h-[120px]"
                    />
                  </div>
                  
                  <Button 
                    type="submit"
                    disabled={contactSubmitting}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold"
                  >
                    {contactSubmitting ? 'Sending...' : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Support Boundaries Notice */}
          <Card className="bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-black/50 rounded-lg shrink-0">
                  <AlertCircle className="w-6 h-6 text-[#a3a3a3]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">A note on support</h3>
                  <p className="text-[#a3a3a3] leading-relaxed">
                    CNC-Helpdesk does not provide machine-specific setup or tuning support.
                  </p>
                  <p className="text-[#a3a3a3] mt-2">
                    The platform is designed to offer general CNC guidance, calculations, and best-practice recommendations.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Footer - Trust Statement */}
          <Card className="bg-black/60 backdrop-blur-sm border-[#e67e22]/20 border">
            <CardContent className="pt-8 pb-8">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-[#e67e22]/20 rounded-full">
                    <Heart className="w-8 h-8 text-[#e67e22]" />
                  </div>
                </div>
                <h2 className="text-xl font-bold text-white mb-3">Your feedback matters</h2>
                <p className="text-[#a3a3a3] max-w-xl mx-auto">
                  CNC-Helpdesk grows and improves through the input of real users like you. 
                  Whether it is a bug, an idea, or just a thought — we appreciate you taking the time to share it.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Footer spacer */}
          <div className="h-8"></div>
        </div>
    </div>
  );
};

export default ContactFeedback;
